import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Enterprise } from '../../entity/entity.types';
import { AllEnterprisesModal } from '../../modals/entity-lists/AllEnterprisesModal';
import { withUser } from '../../user/UserContext';
import { fetchEntrepriseOpponents } from '../enterprise.api';
import { doctrineAnalytics } from '../../utils/analyticsUtils';
import { EntityInfos } from '../../entity/expertises/infos/EntityInfos';
import { EnterpriseList } from '../../entity/entity-lists/EnterpriseList';
import { EntityDecisionListModal } from '../../modals/entity-lists/EntityDecisionListModal';
import { RestrictedModal } from '../../components/modal/components/modal-restricted/RestrictedModal';
import { withModal, WithModalProps } from '../../components/modal/withModal';
import { UserContextType } from '../../user/user.types';
import { InjectedIntlProps } from '../../doctrine.types';
import { withEnterprise } from './EnterpriseContext';
import { EnterpriseContextType } from './enterprise.types';
import {
  EntityBlock,
  EntityBlockTitle,
} from '../../entity/components/EntityBlockComponents';
import { SeeMore } from '../../components/see-more/SeeMore';

type Props = {
  displayTitle: Function;
} & UserContextType &
  WithModalProps &
  InjectedIntlProps &
  EnterpriseContextType;

type State = {
  oppositeEnterprises: Array<Enterprise>;
  hasMore: boolean;
};

export class RawEnterpriseOpponents extends React.Component<Props, State> {
  state: State = {
    oppositeEnterprises: [],
    hasMore: false,
  };

  source = 'opposite_enterprises';

  wording = this.props.intl.formatMessage(
    { id: 'app.pages.enterprise.opponents.wording' },
    { companyName: this.props.enterpriseInfos.raisonSociale },
  );

  async componentDidMount() {
    const {
      enterpriseInfos: { siren },
      displayTitle,
    } = this.props;
    const { oppositeEnterprises, hasMore } = (
      await fetchEntrepriseOpponents(siren)
    ).data;
    this.setState({ oppositeEnterprises, hasMore });
    if (oppositeEnterprises.length > 0) {
      displayTitle();
    }
  }

  handleSeeMoreClick = () => {
    const {
      openModal,
      enterpriseInfos: { siren, raisonSociale },
      accessPremium,
    } = this.props;
    if (accessPremium) {
      openModal(modalProps => (
        <AllEnterprisesModal
          {...modalProps}
          siren={siren}
          source={this.source}
          raisonSociale={raisonSociale}
          wording={this.wording}
        />
      ));
    } else {
      openModal(modalProps => (
        <RestrictedModal {...modalProps} source={`${this.source}_see_more`} />
      ));
    }
    doctrineAnalytics('Enterprise SeeMoreEnterprises', {
      siren,
    });
  };

  handleClickDecision = (enterprise: Enterprise) => {
    const {
      accessPremium,
      openModal,
      enterpriseInfos: { siren },
    } = this.props;
    if (accessPremium) {
      openModal(modalProps => (
        <EntityDecisionListModal
          {...modalProps}
          entityName={enterprise.name}
          wording={this.wording}
          params={{
            oppositeSiren: enterprise.siren,
            siren,
          }}
          source={this.source}
        />
      ));
    } else {
      openModal(modalProps => (
        <RestrictedModal {...modalProps} source={`${this.source}_decision`} />
      ));
    }
    doctrineAnalytics('Entity SeeEntityDecisions', {
      source: this.source,
    });
  };

  render() {
    const {
      experience: { decisionCount },
      enterpriseInfos: { raisonSociale },
    } = this.props;
    const { oppositeEnterprises, hasMore } = this.state;
    if (!oppositeEnterprises.length) return null;
    return (
      <EntityBlock>
        <EntityBlockTitle>
          <FormattedMessage
            id="app.pages.enterprise.opponents.title"
            values={{ numberOfEnterprises: oppositeEnterprises.length }}
          />
        </EntityBlockTitle>
        <EntityInfos
          decisionCount={decisionCount}
          entityType="enterprise"
          entityName={raisonSociale}
        />
        <EnterpriseList
          enterprises={oppositeEnterprises}
          source={this.source}
          wording={this.wording}
          handleClickDecision={(enterprise: Enterprise) =>
            this.handleClickDecision(enterprise)
          }
        />
        {hasMore && (
          <SeeMore onClick={this.handleSeeMoreClick}>
            <FormattedMessage id="app.pages.enterprise.seeMore" />
          </SeeMore>
        )}
      </EntityBlock>
    );
  }
}

export const EnterpriseOpponents = withEnterprise(
  withUser(withModal(injectIntl(RawEnterpriseOpponents))),
);
